/*
 * Scroll to the top when the route changes.
 *
 * ☠️ **A single-page app does not reset the scroll position on navigation** — the browser only
 * does that for a real document load. Without this, following "Wie wir arbeiten" from halfway
 * down a bill page lands the reader halfway down `/methodik`, on a paragraph they did not ask
 * for. Reported 2026-09-12; it had been true of every link on the site since the rebuild began.
 *
 * ⚠️ **A hash is left alone, deliberately.** `/methodik#grundregel` must land on that section,
 * and the twelve entries of that page's table of contents are exactly such links. Scrolling to
 * the top here would re-break what commit `0166db3` fixed — the `<base href>` defect that left
 * every in-page anchor on the site dead for four months. `check:frontend` asserts that
 * behaviour, so this would fail the build, but the reasoning should not have to be
 * rediscovered from a red CI run.
 *
 * An effect on `pathname` rather than `<ScrollRestoration>`: that component also *restores* a
 * remembered position on back-navigation. Right for a browser, wrong here — returning to a
 * list should put you where you left it, which is what the browser's own history already does.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router';

export function useScrollToTop(): void {
    const { pathname, hash } = useLocation();

    useEffect(() => {
        if (hash) return;
        // `instant`, not `smooth`: a page that scrolls itself after a click reads as the page
        // moving under the reader, and it fights anyone who has already started scrolling.
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [pathname, hash]);
}
